import React from 'react';
import { Mail, Phone, MapPin, Users, GraduationCap, User, Rocket } from 'lucide-react';
import { getText } from '../data/content';
import { theme, combineClasses, getCardStyle, getButtonStyle } from '../theme/theme';

/**
 * ============================================================================
 * 页面：联系我们 - Contact.jsx
 * ============================================================================
 *
 * 【作用】联系方式弹窗内容：联系信息（地址、邮箱、电话、办公室）、加入我们（职位列表、申请按钮）。本组件作为弹窗主体被 App 引用。
 * 【如何修改】所有文案与链接在 src/data/content-contact.js。
 *
 * 【参数说明】props: lang（"en"|"cn"）, themeMode（"light"|"dark"）。根据 themeMode 区分亮色/暗色背景、文字与边框样式。
 */
const ContactView = ({ lang, themeMode = "dark", onChangePage }) => {
  const isLight = themeMode === "light";
  // 从 content 取联系信息并展平为当前语言的 address/email/phone/office
  const contactInfoRaw = getText('contact.info', lang);
  const contactInfo = contactInfoRaw && typeof contactInfoRaw === 'object' ? {
    address: contactInfoRaw.address?.[lang] || contactInfoRaw.address?.en || contactInfoRaw.address || '',
    email: contactInfoRaw.email || '',
    phone: contactInfoRaw.phone?.[lang] || contactInfoRaw.phone?.en || contactInfoRaw.phone || '',
    office: contactInfoRaw.office?.[lang] || contactInfoRaw.office?.en || contactInfoRaw.office || ''
  } : {};
  
  // 获取加入我们信息
  const joinUsRaw = getText('contact.joinUs', lang);
  const joinUs = joinUsRaw && typeof joinUsRaw === 'object' ? {
    title: joinUsRaw.title?.[lang] || joinUsRaw.title?.en || joinUsRaw.title || '',
    subtitle: joinUsRaw.subtitle?.[lang] || joinUsRaw.subtitle?.en || joinUsRaw.subtitle || '',
    positions: joinUsRaw.positions?.map(pos => ({
      title: pos.title?.[lang] || pos.title?.en || pos.title || '',
      desc: pos.desc?.[lang] || pos.desc?.en || pos.desc || ''
    })) || [],
    applyButton: joinUsRaw.applyButton?.[lang] || joinUsRaw.applyButton?.en || joinUsRaw.applyButton || '',
    applyLink: joinUsRaw.applyLink || ''
  } : {};

  return (
    <div className="py-4 px-2 max-w-6xl mx-auto">
      {/* 页面标题：按 themeMode 区分亮/暗色文字，目前不显示 */}
      {/*
      <div className="text-center mb-16">
        <h2 className={combineClasses(
          "text-4xl md:text-6xl font-bold mb-4",
          isLight ? "text-gray-900" : "text-white"
        )}>
          {getText('contact.title', lang)}
        </h2>
        <p className={combineClasses(
          "text-lg max-w-2xl mx-auto",
          isLight ? "text-gray-600" : "text-gray-400"
        )}>
          {getText('contact.subtitle', lang)}
        </p>
      </div>
      */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
        {/* 左侧：联系方式 */}
        <div className="space-y-8">
          <h3 className={combineClasses(
            "text-2xl font-bold mb-6",
            isLight ? "text-gray-900" : "text-white"
          )}>
            {lang === 'cn' ? '联系信息' : 'Contact Information'}
          </h3>

          {/*
            【可选】地址上方展示图：学校图 + 中国位置图。
            - 整段不显示：把下面「图片容器」到「地址」之间的整块用 JSX 块注释包起来，或删掉；只隐藏一张图就注释/删掉对应那个 img 的外层 div。
            - 图片放置：public/images/contact/，路径写 /images/contact/xxx.jpg。
            - 高度：由图片自身决定，行高=两图中较高的那张，保证最高的图完整显示；宽度两列等分（grid-cols-2），随容器自动变化。
            - 圆角/边框：见下方 img className；与下方地址间距：改 mb-*。
          */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-2 items-start">
            {/* 学校图：可单独注释掉。列宽等分，高度随图片比例，不裁剪。 */}
            <div className="min-w-0">
              <img
                src="/images/contact/school.jpg"
                alt={lang === 'cn' ? '学校' : 'School'}
                className={combineClasses(
                  "w-full h-auto block rounded-xl border",
                  isLight ? "border-gray-200" : "border-white/10"
                )}
              />
            </div>
            {/* 中国位置图：同上。 */}
            <div className="min-w-0">
              <img
                src="/images/contact/location-china.jpg"
                alt={lang === 'cn' ? '在中国的位置' : 'Location in China'}
                className={combineClasses(
                  "w-full h-auto block rounded-xl border",
                  isLight ? "border-gray-200" : "border-white/10"
                )}
              />
            </div>
          </div>

          {/* 地址 */}
          <div className={combineClasses(
            "flex items-start gap-4 p-6 rounded-xl border hover:border-cyan-500/30 transition-all",
            isLight ? "bg-gray-50 border-gray-200" : "bg-white/5 border-white/10"
          )}>
            <div className={combineClasses(
              "p-3 rounded-lg flex-shrink-0 border",
              isLight ? "bg-cyan-50 border-cyan-200" : "bg-cyan-500/20 border-cyan-500/30"
            )}>
              <MapPin className={combineClasses("w-6 h-6", isLight ? "text-cyan-600" : "text-cyan-400")} />
            </div>
            <div>
              <h4 className={combineClasses("font-semibold mb-2", isLight ? "text-gray-900" : "text-white")}>
                {lang === 'cn' ? '地址' : 'Address'}
              </h4>
              <p className={combineClasses("leading-relaxed", isLight ? "text-gray-700" : "text-gray-300")}>
                {contactInfo?.address}
              </p>
              <p className={combineClasses("text-sm mt-1", isLight ? "text-gray-500" : "text-gray-400")}>
                {contactInfo?.office}
              </p>
            </div>
          </div>

          {/* 邮箱 */}
          <div className={combineClasses(
            "flex items-start gap-4 p-6 rounded-xl border hover:border-cyan-500/30 transition-all",
            isLight ? "bg-gray-50 border-gray-200" : "bg-white/5 border-white/10"
          )}>
            <div className={combineClasses(
              "p-3 rounded-lg flex-shrink-0 border",
              isLight ? "bg-purple-50 border-purple-200" : "bg-purple-500/20 border-purple-500/30"
            )}>
              <Mail className={combineClasses("w-6 h-6", isLight ? "text-purple-600" : "text-purple-400")} />
            </div>
            <div>
              <h4 className={combineClasses("font-semibold mb-2", isLight ? "text-gray-900" : "text-white")}>
                {lang === 'cn' ? '邮箱' : 'Email'}
              </h4>
              <a
                href={`mailto:${contactInfo?.email}`}
                className={combineClasses(
                  "transition-colors",
                  isLight ? "text-cyan-600 hover:text-gray-900" : "text-cyan-400 hover:text-white"
                )}
              >
                {contactInfo?.email}
              </a>
            </div>
          </div>

          {/* 电话 */}
          <div className={combineClasses(
            "flex items-start gap-4 p-6 rounded-xl border hover:border-cyan-500/30 transition-all",
            isLight ? "bg-gray-50 border-gray-200" : "bg-white/5 border-white/10"
          )}>
            <div className={combineClasses(
              "p-3 rounded-lg flex-shrink-0 border",
              isLight ? "bg-emerald-50 border-emerald-200" : "bg-emerald-500/20 border-emerald-500/30"
            )}>
              <Phone className={combineClasses("w-6 h-6", isLight ? "text-emerald-600" : "text-emerald-400")} />
            </div>
            <div>
              <h4 className={combineClasses("font-semibold mb-2", isLight ? "text-gray-900" : "text-white")}>
                {lang === 'cn' ? '电话' : 'Phone'}
              </h4>
              <a
                href={`tel:${contactInfo?.phone}`}
                className={combineClasses(
                  "transition-colors",
                  isLight ? "text-cyan-600 hover:text-gray-900" : "text-cyan-400 hover:text-white"
                )}
              >
                {contactInfo?.phone}
              </a>
            </div>
          </div>
        </div>

        {/* 右侧：加入我们 */}
        <div className="space-y-8">
          <div>
            <h3 className={combineClasses(
              "text-2xl font-bold mb-2",
              isLight ? "text-gray-900" : "text-white"
            )}>
              {joinUs?.title}
            </h3>
            <p className={combineClasses("mb-8", isLight ? "text-gray-600" : "text-gray-400")}>
              {joinUs?.subtitle}
            </p>
          </div>

          {/* 招聘职位列表：使用 getCardStyle 以随 themeMode 切换卡片样式 */}
          <div className="space-y-6">
            {joinUs?.positions?.map((position, index) => (
              <div
                key={index}
                className={combineClasses(
                  getCardStyle(themeMode),
                  "p-6 hover:border-cyan-500/30 transition-all"
                )}
              >
                <div className="flex items-start gap-4">
                  <div className={combineClasses(
                    "p-3 rounded-lg bg-gradient-to-br flex-shrink-0 border",
                    isLight
                      ? "from-cyan-100 to-purple-100 border-gray-200"
                      : "from-cyan-500/20 to-purple-500/20 border-white/10"
                  )}>
                    {index === 0 ? (
                      <GraduationCap className={combineClasses("w-6 h-6", isLight ? "text-cyan-600" : "text-cyan-400")} />
                    ) : index === 1 ? (
                      <User className={combineClasses("w-6 h-6", isLight ? "text-purple-600" : "text-purple-400")} />
                    ) : (
                      <Users className={combineClasses("w-6 h-6", isLight ? "text-emerald-600" : "text-emerald-400")} />
                    )}
                  </div>
                  <div className="flex-1">
                    <h4 className={combineClasses(
                      "text-xl font-bold mb-2",
                      isLight ? "text-gray-900" : "text-white"
                    )}>
                      {position.title}
                    </h4>
                    <p className={combineClasses(
                      "leading-relaxed",
                      isLight ? "text-gray-700" : "text-gray-300"
                    )}>
                      {position.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* 申请按钮：使用 getButtonStyle 以随 themeMode 切换按钮样式 */}
          <div className="pt-6">
            <button
              type="button"
              onClick={() => onChangePage("openings")}
              className={combineClasses(
                getButtonStyle(themeMode, 'tertiary'),
                "inline-flex items-center justify-center gap-2"
              )}
            >
              <Rocket className="w-5 h-5" />
              {joinUs?.applyButton}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactView;
